<template>
  <v-app>
    <!-- Search Panel -->
    <v-container fluid>
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-title
            color="blue-grey-lighten-4"
            class="panel-header"
          >
            Search QC Records
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-form @submit.prevent="submit">
              <v-row class="px-2 pt-4">
                <v-col md="3">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    label="From Date"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="from_date.value.value"
                    clearable
                  />
                </v-col>

                <v-col md="3">
                  <v-date-input
                    input-format="dd/mm/yyyy"
                    label="To Date"
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    v-model="to_date.value.value"
                    clearable
                  />
                </v-col>

                <v-col md="2" class="d-flex align-center">
                  <v-btn type="submit" color="primary">Search</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- QC Records List -->
    <v-container fluid style="margin-top:-20px">
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-title
            color="blue-grey-lighten-4"
            class="panel-header"
          >
            QC Records List
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-row class="py-2">
              <v-col md="4">
                <v-text-field
                  v-model="search"
                  label="Search"
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  append-inner-icon="mdi-magnify"
                  clearable
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="headers"
              :items="qcList"
              :search="search"
              :loading="loading"
              density="compact"
            >
              <template #item.index="{ index }">
                {{ index + 1 }}
              </template>

              <template #item.qc_status="{ item }">
                <v-chip
                  size="small"
                  :color="item.qc_status === 'passed' ? 'success' : 'red'"
                >
                  {{ item.qc_status.toUpperCase() }}
                </v-chip>
              </template>

              <template #item.qc_date="{ item }">
                {{ formatDateView(item.qc_date) }}
              </template>

              <template #item.actions="{ item }">
                 <div class="d-flex align-center justify-center" style="gap: 6px;">
                    <v-tooltip text="Edit">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon="mdi-pencil"
                          size="small"
                          variant="text"
                          color="primary"
                          @click="goToEdit(item.id)"
                          v-if="$updateAccess($route.fullPath)"
                        />
                      </template>
                    </v-tooltip>
                    <v-tooltip text="View Details">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon="mdi-eye"
                          size="small"
                          variant="text"
                          color="info"
                          @click="viewDetails(item)"
                          v-if="$viewAccess($route.fullPath)"
                        />
                      </template>
                    </v-tooltip>
                  </div>
              </template>
            </v-data-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card>
        <v-card-title class="bg-blue-grey-lighten-4">
          QC Details - {{ selectedItem?.code }}
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col md="6"><b>PO:</b> {{ selectedItem?.production_order_id }}</v-col>
            <v-col md="6"><b>Style:</b> {{ selectedItem?.style?.code }}</v-col>
            <v-col md="6"><b>Color:</b> {{ selectedItem?.color?.name }}</v-col>
            <v-col md="6"><b>QC Date:</b> {{ formatDateView(selectedItem?.qc_date) }}</v-col>
          </v-row>

          <v-divider class="my-4" />

          <v-table density="compact">
            <thead>
              <tr>
                <th>#</th>
                <th>Size ID</th>
                <th class="text-right">Checked</th>
                <th class="text-right">Passed</th>
                <th class="text-right">Failed</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(d, i) in selectedItem?.details || []"
                :key="i"
              >
                <td>{{ i + 1 }}</td>
                <td>{{ d.size_id }}</td>
                <td class="text-right">{{ d.checked_qty }}</td>
                <td class="text-right">{{ d.passed_qty }}</td>
                <td class="text-right">{{ d.failed_qty }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="tonal" @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useRouter } from "vue-router";
import { formatDateView, formatDateDash } from "@/plugins/helpers";

const store = useStore();

const panel = ref([0]);
const search = ref("");
const loading = ref(false);
const detailsDialog = ref(false);
const selectedItem = ref(null);
const router = useRouter();

const headers = [
  { title: "#", key: "index", sortable: false },
  { title: "QC Code", key: "code" },
  { title: "Style", key: "style.code" },
  { title: "Color", key: "color.name" },
  { title: "Checked", key: "checked_qty" },
  { title: "Passed", key: "passed_qty" },
  { title: "Failed", key: "failed_qty" },
  { title: "QC Date", key: "qc_date" },
  { title: "Status", key: "qc_status" },
  { title: "Actions", key: "actions", sortable: false },
];

const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable(),
    to_date: yup.date().nullable(),
  }),
});

const from_date = useField("from_date");
const to_date = useField("to_date");

const submit = handleSubmit(() => {
  loadList();
});

const qcList = computed(() => {
  return store.getters.getProductionQcList || [];
});

const loadList = () => {
  loading.value = true;
  store.dispatch("getProductionQcList").finally(() => {
    loading.value = false;
  });
};

const viewDetails = (item) => {
  router.push({ name: "qc-view", params: { id: item.id } });
  // selectedItem.value = item;
  // detailsDialog.value = true;
};

const goToEdit = (id) => {
  // router.push(`/qc/entry/${id}`);
  router.push({ name: "qc-entry", params: { id } });
};

onMounted(() => {
  loadList();
});
</script>
